/**
 * Raster tiles from the tiler, kept in the Cache Storage API under a key without the token.
 *
 * The viewer token rides in the tile URL (`token=`, see tokenSource.ts) and lives 15 minutes. The
 * tiler marks every tile immutable, but the browser's HTTP cache is keyed by the full URL, so each
 * renewal made every tile a miss: a pan back over the same ground, the refetch MapView triggers
 * after a refresh, and a reload all downloaded the screen again. Moving the token into an
 * `Authorization` header would fix the key but costs a CORS preflight per distinct tile URL,
 * doubling the requests. So MapLibre is pointed at our own protocol instead (`tileCacheRequest`),
 * whose handler reads and fills a cache keyed by the URL with `token` removed. The `d=` digest
 * forint puts in every tile URL changes when the data does, so a key never outlives its pixels.
 *
 * Cache Storage is missing in a non-secure context (plain http on a LAN host) and can refuse in
 * private windows or when the quota is spent; every such case falls back to a plain fetch, which
 * still has the HTTP cache behind it as before. Only 200 answers are stored. The cache holds at
 * most `MAX_ENTRIES` tiles, trimmed oldest-stored first: Cache Storage keeps insertion order and
 * has no LRU of its own, and a tile read again is not worth a rewrite to mark it fresh.
 */
import type { TokenSource } from './tokenSource';

export const TILE_CACHE_PROTOCOL = 'tiler-cached';
/** Versioned so a change of what is stored can drop the old cache by name. */
export const TILE_CACHE_NAME = 'forestum-tiles-v1';
/** About 50–130 kB per 512 px WebP tile: some 100–250 MB at the cap. */
export const MAX_ENTRIES = 2000;
/** How many stores pass between two trims; a trim lists every key. */
const TRIM_EVERY = 50;

const TILE_PATH = /\/v1\/objects\/[^/]+\/tiles\//;

/** The URL a tile is cached under: `url` without its `token` query parameter, all else kept in order. */
export function cacheKeyOf(url: string): string {
  const q = url.indexOf('?');
  if (q < 0) return url;
  const hash = url.indexOf('#', q);
  const query = url.slice(q + 1, hash < 0 ? undefined : hash);
  const kept = query.split('&').filter((part) => part !== '' && part.split('=')[0] !== 'token');
  return url.slice(0, q) + (kept.length ? `?${kept.join('&')}` : '') + (hash < 0 ? '' : url.slice(hash));
}

/** Whether `url` is a tile of the tiler at `baseUrl`. */
export function isTilerTile(url: string, baseUrl: string): boolean {
  return baseUrl !== '' && url.startsWith(baseUrl) && TILE_PATH.test(url.slice(baseUrl.length));
}

/** An HTTP failure of a tile; MapLibre logs the message and draws nothing there. */
export class TileHttpError extends Error {
  constructor(readonly status: number) {
    super(`tiler ${status}`);
  }
}

export interface TileCache {
  /** The bytes of the tile at `url` (signed or not), from the cache when it holds them. */
  fetch(url: string, signal?: AbortSignal): Promise<ArrayBuffer>;
  /** Drops every stored tile (signing out). */
  clear(): Promise<void>;
}

export interface TileCacheOptions {
  /** The Cache Storage to use; `undefined` (no secure context, an old browser) means none. */
  storage?: CacheStorage;
  fetchImpl?: typeof fetch;
  maxEntries?: number;
}

const defaultStorage = (): CacheStorage | undefined => (typeof caches === 'undefined' ? undefined : caches);

export function createTileCache({ storage = defaultStorage(), fetchImpl = (...args) => fetch(...args), maxEntries = MAX_ENTRIES }: TileCacheOptions = {}): TileCache {
  // Opened once; a refusal (private mode, blocked storage) turns the cache off for the session.
  let opened: Promise<Cache | null> | null = null;
  const open = (): Promise<Cache | null> => {
    opened ??= storage ? storage.open(TILE_CACHE_NAME).catch(() => null) : Promise.resolve(null);
    return opened;
  };
  let stores = 0;
  let trimming: Promise<void> | null = null;

  const trim = (cache: Cache): Promise<void> => {
    trimming ??= (async () => {
      const keys = await cache.keys();
      for (const key of keys.slice(0, Math.max(0, keys.length - maxEntries))) await cache.delete(key);
    })()
      .catch(() => undefined)
      .finally(() => {
        trimming = null;
      });
    return trimming;
  };

  const store = async (cache: Cache, key: string, data: ArrayBuffer, type: string | null): Promise<void> => {
    try {
      // A copy, taken before the first await: MapLibre may transfer or detach the buffer it got.
      await cache.put(key, new Response(data.slice(0), { status: 200, headers: type ? { 'Content-Type': type } : {} }));
      if (++stores % TRIM_EVERY === 1) await trim(cache);
    } catch {
      // Quota spent or storage gone: the tile was served, it just is not kept.
    }
  };

  return {
    async fetch(url, signal) {
      const key = cacheKeyOf(url);
      const cache = await open();
      if (cache) {
        const hit = await cache.match(key).catch(() => undefined);
        if (hit) return hit.arrayBuffer();
      }
      const response = await fetchImpl(url, { signal });
      if (!response.ok) throw new TileHttpError(response.status);
      const data = await response.arrayBuffer();
      if (cache && response.status === 200) void store(cache, key, data, response.headers.get('Content-Type'));
      return data;
    },

    async clear() {
      opened = null;
      await storage?.delete(TILE_CACHE_NAME).catch(() => false);
    },
  };
}

/** The one cache the map, the boundary editor and the CHM readout share. */
export const tileCache = createTileCache();

/** MapLibre's protocol handler for `TILE_CACHE_PROTOCOL` URLs: the wrapped tiler URL through the cache. */
export async function serveCachedTile({ url }: { url: string }, abort: AbortController): Promise<{ data: ArrayBuffer }> {
  return { data: await tileCache.fetch(url.slice(TILE_CACHE_PROTOCOL.length + 3), abort.signal) };
}

/**
 * MapLibre's `transformRequest` for a map showing tiler rasters: the token source's, with tiler
 * tiles routed through `TILE_CACHE_PROTOCOL`. A tile goes there even while no live token is held
 * (the token source then adds none): a cached tile needs none, and an uncached one fails as it
 * would have, for the refresh to bring it back.
 */
export function tileCacheRequest(tokenSource: Pick<TokenSource, 'transformRequest' | 'baseUrl'>) {
  return (url: string, resourceType?: string): { url: string } | undefined => {
    const signed = tokenSource.transformRequest(url, resourceType);
    if (resourceType !== 'Tile' || !isTilerTile(url, tokenSource.baseUrl)) return signed;
    return { url: `${TILE_CACHE_PROTOCOL}://${signed?.url ?? url}` };
  };
}
